(function (angular, app) {
    'use strict';

    app.service('GoogleLoginService', ['$rootScope', 'Api', '$q', 'User', 'Config', '$window', '$timeout',
        function ($rootScope, Api, $q, User, Config, $window, $timeout) {
            var SCOPE = 'email profile https://www.googleapis.com/auth/user.phonenumbers.read';
            var AUTH_PROXY_MESSAGE_TYPE = 'SP_GOOGLE_AUTH_PROXY_RESULT';
            var self = this;
            var loginSuccess = false;

            angular.extend(self, {
                loginWithGoogle: loginWithGoogle,
            });

            function loginWithGoogle(code) {
                loginSuccess = false;
                return getLoginRequestData(code)
                    .then(loginWithGoogleRequest)
                    .then(function(response) {
                        return _handleGoogleLoginResponse(response);
                    })
                    .catch(function(error) {
                        loginSuccess = false;
                        return $q.reject(error);
                    })
                    .finally(function() {
                        _handleFinally();
                    });
            }

            function loadGoogleScriptIfNeeded() {
                if ($window.google && $window.google.accounts && $window.google.accounts.oauth2) {
                    return $q.when();
                }

                var deferred = $q.defer();
                var script = document.createElement('script');
                script.src = 'https://accounts.google.com/gsi/client';
                script.async = true;
                script.defer = true;
                script.onload = function() {
                    $timeout(function() {
                        deferred.resolve();
                    }, 500);
                };
                script.onerror = function() {
                    deferred.reject(new Error('Unable to load Google Identity Services script'));
                };
                document.head.appendChild(script);
                return deferred.promise;
            }

            function requestAuthCodeDirectly(clientId) {
                return loadGoogleScriptIfNeeded().then(function() {
                    var deferred = $q.defer();

                    try {
                        var codeClient = $window.google.accounts.oauth2.initCodeClient({
                            client_id: clientId,
                            scope: SCOPE,
                            ux_mode: 'popup',
                            callback: function(response) {
                                if (response && response.code) {
                                    return deferred.resolve(response.code);
                                }

                                deferred.reject(new Error('GOOGLE_LOGIN_NO_AUTH_CODE'));
                            }
                        });

                        codeClient.requestCode();
                    } catch (err) {
                        deferred.reject(err);
                    }

                    return deferred.promise;
                });
            }

            function getGoogleAuthProxyUrl() {
                var retailerId = Config.retailer && Config.retailer.id;
                var settings = Config.retailer && Config.retailer.settings;
                var googleAuthProxyUrl = (settings && settings.googleAuthProxyUrl) || 'https://api.self-point.com';

                if (!googleAuthProxyUrl || !retailerId) {
                    return null;
                }

                return googleAuthProxyUrl.replace(/\/$/, '') + '/v2/retailers/' + retailerId + '/sessions/google/auth-proxy';
            }

            function getCurrentOrigin() {
                return $window.location.origin || ($window.location.protocol + '//' + $window.location.host);
            }

            function getUrlOrigin(url) {
                var link = document.createElement('a');
                link.href = url;
                return link.protocol + '//' + link.host;
            }

            function appendQueryParams(url, params) {
                var query = Object.keys(params)
                    .filter(function(key) {
                        return params[key] !== undefined && params[key] !== null;
                    })
                    .map(function(key) {
                        return encodeURIComponent(key) + '=' + encodeURIComponent(params[key]);
                    })
                    .join('&');

                return url + (url.indexOf('?') === -1 ? '?' : '&') + query;
            }

            function createState() {
                return String(Date.now()) + String(Math.random()).slice(2);
            }

            function requestAuthCodeThroughProxy(authProxyUrl, clientId) {
                var deferred = $q.defer();
                var state = createState();
                var proxyOrigin = getUrlOrigin(authProxyUrl);
                var completed = false;
                var popupChecker;
                var timeoutPromise;

                var popup = $window.open(
                    appendQueryParams(authProxyUrl, {
                        client_id: clientId,
                        scope: SCOPE,
                        origin: getCurrentOrigin(),
                        state: state
                    }),
                    'googleLogin',
                    'width=500,height=650'
                );

                if (!popup) {
                    return $q.reject(new Error('GOOGLE_LOGIN_POPUP_BLOCKED'));
                }

                function cleanup() {
                    completed = true;
                    $window.removeEventListener('message', onMessage);
                    if (popupChecker) {
                        $window.clearInterval(popupChecker);
                    }
                    if (timeoutPromise) {
                        $timeout.cancel(timeoutPromise);
                    }
                }

                function reject(error) {
                    cleanup();
                    deferred.reject(error);
                }

                function onMessage(event) {
                    var data = event.data || {};

                    if (completed || event.origin !== proxyOrigin || data.type !== AUTH_PROXY_MESSAGE_TYPE || data.state !== state) {
                        return;
                    }

                    cleanup();

                    try {
                        popup.close();
                    } catch (err) {}

                    if (data.error) {
                        return deferred.reject(new Error(data.error));
                    }

                    if (!data.code) {
                        return deferred.reject(new Error('GOOGLE_LOGIN_NO_AUTH_CODE'));
                    }

                    deferred.resolve(data.code);
                }

                $window.addEventListener('message', onMessage);

                popupChecker = $window.setInterval(function() {
                    if (completed || !popup.closed) {
                        return;
                    }

                    $timeout(function() {
                        reject(new Error('GOOGLE_LOGIN_POPUP_CLOSED'));
                    });
                }, 500);

                timeoutPromise = $timeout(function() {
                    reject(new Error('GOOGLE_LOGIN_TIMEOUT'));
                }, 120000);

                return deferred.promise;
            }

            function requestAuthCode() {
                var googleClientId = Config.retailer && Config.retailer.settings && Config.retailer.settings.googleClientId;
                var isUseDefaultGoogleLoginAccount = Config.retailer && Config.retailer.settings && Config.retailer.settings.isUseDefaultGoogleLoginAccount;

                if (isUseDefaultGoogleLoginAccount === 'true') {
                    var authProxyUrl = getGoogleAuthProxyUrl();
                    if (authProxyUrl) {
                        return requestAuthCodeThroughProxy(authProxyUrl, googleClientId);
                    }
                } else if (googleClientId) {
                    return requestAuthCodeDirectly(googleClientId);
                }
                
                return $q.reject(new Error('Google Login is not enabled'));
            }

            function getLoginRequestData(code) {
                if (code) {
                    return $q.when({ code: code });
                }

                return requestAuthCode().then(function(authCode) {
                    return { code: authCode };
                });
            }

            function loginWithGoogleRequest(requestData) {
                return Api.request({
                    method: 'POST',
                    url: requestData.url || '/v2/retailers/:rid/sessions/google',
                    params: {
                        platform: 'WEB'
                    },
                    data: { code: requestData.code },
                },{ withoutToast: true  });
            }

            function _handleGoogleLoginResponse(response) {
                if(response.data) {
                    return $q.resolve(response);
                }
                
                var defer = $q.defer();
                loginSuccess = true;
                User.userLoggedInSuccessfullySetSession(response, defer, response.email);
                return defer.promise;
            }

            function _handleFinally() {
                $rootScope.$emit($rootScope.SP_SERVICES.MIXPANEL_EVENTS_LISTENERS.MIXPANEL_LOGIN_ATTEMPT, {
                    loginMethod: 'Google',
                    loginSuccess: loginSuccess,
                    OTPSent: false
                });
            }
        },
    ]);
})(angular, app);